/**
 * videoZoneService.js
 *
 * Spatial video zones for the Matterport tour.
 * Each zone in locationVideoConfig.json is rendered as a THREE.js video plane
 * placed directly in Matterport world space (like a TV screen on a wall).
 *
 * Playback is proximity-based:
 *   - Video starts  when the user enters  `refDistance` metres of the zone origin.
 *   - Video pauses  when the user travels beyond `maxDistance` metres.
 *
 * Audio is NOT coupled to the HTMLVideoElement directly (video.muted = true).
 * Instead, a MediaElementAudioSourceNode is routed through audioService so that
 * the existing distance-based gain math in audioService.updateListener() handles
 * spatial volume falloff automatically — exactly like the ambient audio zones.
 *
 * Usage:
 *   videoZoneService.initialize(sdk)          — call once after SDK connects
 *   videoZoneService.updateListener(position) — call on every Camera.pose update
 *   videoZoneService.destroy()                — call on component unmount
 *
 * Config: public/config/locationVideoConfig.json
 * Feature gate: PROJECT_CONFIG.features.videosEnabled
 */

import audioService from './audioService';
import { assetUrl } from '../utils/assetUrl';

const COMPONENT_NAME = 'mp.videoPlane';

// Module-level registries.
// DOM objects / mesh references cannot pass through the Matterport SDK input
// system (serialized/dropped). We look them up here in onInit / updateZoneConfig.
const _videoRegistry = new Map(); // zoneId → HTMLVideoElement
const _meshRegistry  = new Map(); // zoneId → THREE.Mesh  (set during onInit)

// ─── Video Plane Component ────────────────────────────────────────────────────
// Plain function constructor — required by the Matterport Bundle SDK.
// Positional/dimensional data is passed via SDK inputs (all serializable).
// The video element is retrieved from _videoRegistry by zoneId in onInit.

function VideoPlaneComponent() {
  // Positional + dimensional data — set by addComponent(COMPONENT_NAME, inputs)
  this.inputs = {
    zoneId:    '',
    worldX:    0,
    worldY:    0,
    worldZ:    0,
    worldRotY: 0,
    width:     4,
    height:    2.25,
  };
  this._mesh    = null;
  this._mat     = null;
  this._texture = null;
}

VideoPlaneComponent.prototype.onInit = function () {
  console.log('🎬 VideoPlaneComponent.onInit called, zoneId:', this.inputs.zoneId);
  try {
    const THREE    = this.context.three;
    const { zoneId, worldX, worldY, worldZ, worldRotY, width, height } = this.inputs;
    const video    = _videoRegistry.get(zoneId);

    if (!video) {
      console.warn(`🎬 VideoPlaneComponent: no video element registered for zone "${zoneId}"`);
      return;
    }

    this._texture = new THREE.VideoTexture(video);
    this._texture.minFilter = THREE.LinearFilter;
    this._texture.magFilter = THREE.LinearFilter;
    this._texture.needsUpdate = true;

    const geo  = new THREE.PlaneGeometry(width, height);
    this._mat  = new THREE.MeshBasicMaterial({
      map:   this._texture,
      side:  THREE.DoubleSide,
      color: 0xffffff,
    });
    // Stash THREE reference for live geometry rebuilds from VideoDebugPanel
    this._mat.userData._THREE = THREE;
    this._mesh = new THREE.Mesh(geo, this._mat);
    this._mesh.position.set(worldX, worldY, worldZ);
    this._mesh.rotation.y = worldRotY;

    // Add directly to the THREE.js scene — same pattern as bundleHotspotService
    this.context.scene.add(this._mesh);
    this.outputs.objectRoot = this._mesh;
    this.outputs.collider   = this._mesh;

    // Register mesh so updateZoneConfig can mutate it without accessing SDK internals
    _meshRegistry.set(zoneId, this._mesh);

    // Expose for console debugging
    window._videoDebugMesh = this._mesh;

    console.log(`🎬 VideoPlane placed at (${worldX.toFixed(2)}, ${worldY.toFixed(2)}, ${worldZ.toFixed(2)}) rotY=${worldRotY.toFixed(2)}rad`);
  } catch (err) {
    console.error('🎬 VideoPlaneComponent.onInit failed:', err?.message, err);
  }
};

// onTick is called every render frame by the Matterport SDK.
// THREE.VideoTexture.needsUpdate must be set to true each frame that the video
// has new data — Matterport's internal renderer does NOT call texture.update()
// on externally-added meshes, so we force it here.
VideoPlaneComponent.prototype.onTick = function () {
  if (!this._texture) return;
  const video = this._texture.image;
  if (video && video.readyState >= video.HAVE_CURRENT_DATA) {
    this._texture.needsUpdate = true;
  }
};

VideoPlaneComponent.prototype.onDestroy = function () {
  if (this._mesh) {
    if (this.context?.scene) this.context.scene.remove(this._mesh);
    this._mesh.geometry?.dispose();
  }
  if (this._mat) {
    this._mat.map?.dispose();
    this._mat.dispose();
  }
  this._texture = null;
};

// ─── Service ────────────────────────────────────────────────────────────────

class VideoZoneService {
  constructor() {
    this._sdk        = null;
    this._zones      = [];        // zone config objects from JSON
    this._instances  = new Map(); // zoneId → { sceneObject, videoElement, playing }
    this._registered = false;
    this._configUrl  = '/config/locationVideoConfig.json';

    // Expose for console debugging
    window._videoZoneService = this;
  }

  // ── Initialize ─────────────────────────────────────────────────────────────

  async initialize(sdk) {
    this._sdk = sdk;

    // Register the component factory with the SDK (once per app lifetime).
    // Must be awaited — createObjects will fail if registration hasn't completed.
    // The factory captures this._pendingZoneData into the component instance so
    // onInit can access the videoElement (DOM objects can't go through SDK inputs).
    if (!this._registered) {
      try {
        await sdk.Scene.register(COMPONENT_NAME, function () {
          return new VideoPlaneComponent();
        });
        this._registered = true;
        console.log('🎬 VideoZoneService: video plane component registered');
      } catch (err) {
        console.warn('🎬 VideoZoneService: failed to register component:', err?.message);
        return;
      }
    }

    // Fetch and parse config
    try {
      const resp = await fetch(this._configUrl + '?t=' + Date.now());
      if (!resp.ok) throw new Error(`HTTP ${resp.status} fetching ${this._configUrl}`);
      const config = await resp.json();
      // Only handle mp4 zones for now
      this._zones = (config.zones || []).filter(z => z.type === 'mp4');
      console.log(`🎬 VideoZoneService: loaded ${this._zones.length} MP4 video zone(s)`);
    } catch (err) {
      console.warn('🎬 VideoZoneService: failed to load config:', err?.message);
      return;
    }

    // Create video planes for all zones immediately (world-space coords, no camera pos needed)
    for (const zone of this._zones) {
      await this._createMP4Zone(zone).catch(err =>
        console.warn(`🎬 VideoZoneService: zone "${zone.id}" setup failed:`, err?.message)
      );
    }
  }

  // ── Zone creation ──────────────────────────────────────────────────────────

  async _createMP4Zone(zone) {
    // Create an off-screen video element.
    // video.muted = true is mandatory for browser autoplay policy.
    // Actual audio is routed through audioService via MediaElementAudioSourceNode.
    const video = document.createElement('video');

    // URL-encode only the filename portion so spaces and special characters don't
    // cause the browser to reject the src (e.g. double-space in "AMALNER  LIVE").
    const rawPath = assetUrl(zone.videoSrc);
    const lastSlash = rawPath.lastIndexOf('/');
    const encodedSrc = rawPath.slice(0, lastSlash + 1) +
      encodeURIComponent(rawPath.slice(lastSlash + 1));
    video.src         = encodedSrc;
    video.loop        = zone.loop ?? true;
    video.muted       = true;
    video.crossOrigin = 'anonymous';
    video.playsInline = true;
    video.preload     = 'auto';
    Object.assign(video.style, {
      position: 'absolute',
      width: '1px',
      height: '1px',
      opacity: '0',
      pointerEvents: 'none',
    });
    document.body.appendChild(video);

    console.log(`🎬 VideoZoneService: loading video src: ${encodedSrc}`);

    // Wait for the video to have at least its first frame of data before
    // creating the texture so VideoTexture has something to render immediately.
    // Cap the wait at 5 s so a slow network doesn't block all other zones.
    await new Promise((resolve) => {
      if (video.readyState >= video.HAVE_CURRENT_DATA) { resolve(); return; }
      const onData  = () => { cleanup(); resolve(); };
      const onError = (e) => {
        cleanup();
        console.warn(`🎬 VideoZoneService: video load error for "${zone.id}":`, e?.message ?? e);
        resolve(); // still continue — plane will be invisible until data arrives
      };
      const timeout = setTimeout(() => {
        cleanup();
        console.warn(`🎬 VideoZoneService: video load timed out for "${zone.id}" — plane will update once data arrives`);
        resolve();
      }, 5000);
      const cleanup = () => {
        video.removeEventListener('loadeddata', onData);
        video.removeEventListener('error', onError);
        clearTimeout(timeout);
      };
      video.addEventListener('loadeddata', onData);
      video.addEventListener('error', onError);
    });

    // Compute plane dimensions from config
    const aspectRatio = zone.aspectRatio ?? (16 / 9);
    const width       = zone.width       ?? 3;
    const height      = width / aspectRatio;
    const rotY        = ((zone.rotation?.y ?? 0) * Math.PI) / 180;

    // Store the video element in the module-level registry keyed by zone.id.
    // DOM objects cannot pass through the SDK input system (serialized/dropped)
    // so we retrieve them from _videoRegistry inside onInit.
    _videoRegistry.set(zone.id, video);

    const [sceneObject] = await this._sdk.Scene.createObjects(1);
    const node = sceneObject.addNode();
    // Pass all serializable data via inputs — the SDK sets this.inputs before onInit fires.
    node.addComponent(COMPONENT_NAME, {
      zoneId:    zone.id,
      worldX:    zone.position.x,
      worldY:    zone.position.y,
      worldZ:    zone.position.z,
      worldRotY: rotY,
      width,
      height,
    });

    sceneObject.start();

    // onInit fired synchronously during start() — mesh is now in _meshRegistry
    const mesh = _meshRegistry.get(zone.id) ?? null;
    this._instances.set(zone.id, { sceneObject, videoElement: video, playing: false, mesh });
    console.log(`🎬 VideoZoneService: zone "${zone.id}" ready — mesh: ${mesh ? 'ok' : 'MISSING'}`);
  }

  // ── Proximity update (called on every Camera.pose, throttled by matterportService) ──

  updateListener(position) {
    for (const zone of this._zones) {
      const inst = this._instances.get(zone.id);
      if (!inst) continue;

      const { videoElement } = inst;

      const dx   = position.x - zone.position.x;
      const dy   = position.y - zone.position.y;
      const dz   = position.z - zone.position.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

      const ref = zone.refDistance ?? 6;
      const max = zone.maxDistance ?? 18;

      if (dist <= ref && !inst.playing) {
        // Entered zone — play video and register spatial audio
        videoElement.play().catch(err =>
          console.warn(`🎬 Zone "${zone.id}": video.play() rejected:`, err?.message)
        );
        audioService.registerVideoAudioZone(zone, videoElement);
        inst.playing = true;
        console.log(`🎬 Zone "${zone.id}": ▶ playing (${dist.toFixed(1)}m from zone)`);

      } else if (dist > max && inst.playing) {
        // Left zone — pause video and unregister spatial audio
        videoElement.pause();
        audioService.unregisterVideoAudioZone(zone.id);
        inst.playing = false;
        console.log(`🎬 Zone "${zone.id}": ⏸ paused (${dist.toFixed(1)}m from zone)`);
      }
    }
  }

  // ── Manual play / pause (used by debug panel and "Go to Zone" button) ────────

  /**
   * Force-play a zone regardless of camera distance. Also registers spatial audio
   * if it hasn't been registered yet (unlike the raw videoElement.play() in the panel).
   * @param {string} zoneId
   */
  forcePlay(zoneId) {
    const inst = this._instances.get(zoneId);
    if (!inst) { console.warn(`🎬 VideoZoneService.forcePlay: zone "${zoneId}" not found`); return; }
    const zone = this._zones.find(z => z.id === zoneId);
    const { videoElement } = inst;

    videoElement.play().catch(err =>
      console.warn(`🎬 Zone "${zoneId}": forcePlay() rejected:`, err?.message)
    );
    if (zone && !inst.playing) {
      audioService.registerVideoAudioZone(zone, videoElement);
    }
    inst.playing = true;
    console.log(`🎬 Zone "${zoneId}": ▶ force-playing`);
  }

  /**
   * Force-pause a zone and unregister its spatial audio.
   * @param {string} zoneId
   */
  forcePause(zoneId) {
    const inst = this._instances.get(zoneId);
    if (!inst) { console.warn(`🎬 VideoZoneService.forcePause: zone "${zoneId}" not found`); return; }
    inst.videoElement.pause();
    if (inst.playing) {
      audioService.unregisterVideoAudioZone(zoneId);
      inst.playing = false;
    }
    console.log(`🎬 Zone "${zoneId}": ⏸ force-paused`);
  }

  // ── Cleanup ────────────────────────────────────────────────────────────────

  destroy() {
    for (const [zoneId, inst] of this._instances) {
      try { inst.videoElement.pause(); } catch (_) {}
      try { inst.videoElement.remove(); } catch (_) {}
      try { inst.sceneObject.stop(); } catch (_) {}
      audioService.unregisterVideoAudioZone(zoneId);
    }
    this._instances.clear();
    this._zones = [];
    console.log('🎬 VideoZoneService: destroyed');
  }

  // ── Debug / live-edit API (used by VideoDebugPanel) ───────────────────────

  /** Returns all loaded zone config objects. */
  getZones() {
    return this._zones;
  }

  /**
   * Returns diagnostic info about a zone's runtime state.
   * @param {string} zoneId
   */
  getInstanceStatus(zoneId) {
    const inst = this._instances.get(zoneId);
    if (!inst) return null;
    const v = inst.videoElement;
    return {
      playing:      inst.playing,
      readyState:   v.readyState,       // 0=HAVE_NOTHING … 4=HAVE_ENOUGH_DATA
      currentTime:  v.currentTime,
      duration:     v.duration,
      videoWidth:   v.videoWidth,
      videoHeight:  v.videoHeight,
      src:          v.src,
      error:        v.error?.message ?? null,
    };
  }

  /**
   * Live-patch a zone's config and apply changes to the THREE.js mesh immediately.
   * Supported keys: position {x,y,z}, rotation {y}, width, aspectRatio,
   *                 refDistance, maxDistance, rolloffFactor, volume, loop.
   * @param {string} zoneId
   * @param {object} updates
   */
  updateZoneConfig(zoneId, updates) {
    const zone = this._zones.find(z => z.id === zoneId);
    if (!zone) return;

    if (updates.position) {
      zone.position = { ...zone.position, ...updates.position };
      delete updates.position;
    }
    if (updates.rotation) {
      zone.rotation = { ...zone.rotation, ...updates.rotation };
      delete updates.rotation;
    }
    Object.assign(zone, updates);

    // Move/resize the mesh in the scene
    const inst = this._instances.get(zoneId);
    if (!inst) return;

    // Use the mesh reference stored during onInit (avoids walking private SDK internals)
    const liveMesh = inst.mesh;

    if (liveMesh) {
      liveMesh.position.set(zone.position.x, zone.position.y, zone.position.z);
      liveMesh.rotation.y = ((zone.rotation?.y ?? 0) * Math.PI) / 180;

      // Resize geometry if width or aspectRatio changed
      if (updates.width !== undefined || updates.aspectRatio !== undefined) {
        const w = zone.width ?? 3;
        const h = w / (zone.aspectRatio ?? (16 / 9));
        liveMesh.geometry.dispose();
        const threeRef = liveMesh.material?.userData?._THREE;
        if (threeRef) {
          liveMesh.geometry = new threeRef.PlaneGeometry(w, h);
        }
      }
    }
  }

  /**
   * Re-fetch the config file and recreate all video zones.
   * Tears down existing zones first.
   */
  async reloadConfig() {
    // Tear down existing instances
    for (const [zoneId, inst] of this._instances) {
      try { inst.videoElement.pause(); } catch (_) {}
      try { inst.videoElement.remove(); } catch (_) {}
      try { inst.sceneObject.stop(); } catch (_) {}
      audioService.unregisterVideoAudioZone(zoneId);
    }
    this._instances.clear();
    this._zones = [];

    if (!this._sdk) { console.warn('🎬 VideoZoneService: cannot reload — SDK not connected'); return; }

    console.log('🎬 VideoZoneService: reloading config from', this._configUrl);
    try {
      const resp = await fetch(this._configUrl + '?t=' + Date.now());
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const config = await resp.json();
      this._zones = (config.zones || []).filter(z => z.type === 'mp4');
    } catch (err) {
      console.warn('🎬 VideoZoneService: reload failed:', err?.message);
      return;
    }

    for (const zone of this._zones) {
      await this._createMP4Zone(zone).catch(err =>
        console.warn(`🎬 VideoZoneService: zone "${zone.id}" setup failed:`, err?.message)
      );
    }
    console.log(`🎬 VideoZoneService: reload complete — ${this._zones.length} zone(s)`);
  }
}


const videoZoneService = new VideoZoneService();
export default videoZoneService;
